import { type ClassValue, clsx } from 'clsx';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import { CountryCode } from 'libphonenumber-js';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function currencyFormat(num?: number) {
  return (num ?? 0).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

export async function getFileFromImage(b64img: string, fileName: string) {
  return urltoFile(b64img, fileName, base64MimeType(b64img));
}

// return a promise that resolves with a File instance
function urltoFile(url, filename, mimeType) {
  return fetch(url)
    .then((res) => res.arrayBuffer())
    .then((buf) => new File([buf], filename, { type: mimeType }));
}

// return mime Type of bs64
function base64MimeType(encoded) {
  let result = '';

  if (typeof encoded !== 'string') {
    return result;
  }

  const mime = encoded.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);

  if (mime && mime.length) {
    result = mime[1]!;
  }

  return result;
}

export function slugToTitleCase(slug: string) {
  const separators = ['-', ' ', '_'];
  const separatorRegex = new RegExp(`[${separators.join('')}]`, 'g');
  const words = slug.split(separatorRegex);
  return words
    .map((word) => {
      if (word.length === 0) return ''; // Handle cases with consecutive separators
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export interface CategoryRouteTarget {
  id?: string | null;
  slug?: string | null;
  name?: string | null;
}

export function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim()
  );
}

export function buildCategoryHref(category?: CategoryRouteTarget | null): string {
  const slug = category?.slug?.trim();
  const id = category?.id?.trim();
  const routeSegment = slug || id;

  if (!routeSegment) {
    return '/category';
  }

  const title = category?.name?.trim();
  const titleQuery = !slug && title ? `?title=${encodeURIComponent(title)}` : '';

  return `/category/${encodeURIComponent(routeSegment)}${titleQuery}`;
}

export function generateArrayOfDays(total: number, count: number): number[] {
  let trackBy = Number(total);
  const items: number[] = [];
  while (trackBy > 0) {
    items.push(count);
    trackBy -= count;
  }
  return items;
}

export enum DateScheme {
  base = 'eo, MMM yyyy',
}

export function formatDate(date: Date, scheme: DateScheme = DateScheme.base) {
  return format(date, scheme);
}

export const REGIONS_IN_GHANA = [
  'Ahafo',
  'Ashanti',
  'Bono',
  'Bono East',
  'Central',
  'Eastern',
  'Greater Accra',
  'North East',
  'Northern',
  'Oti',
  'Savannah',
  'Upper East',
  'Upper West',
  'Volta',
  'Western North',
  'Western',
];

export const BIRTH_DAY_OPTIONS = Array.from(
  { length: 31 },
  (_, index) => String(index + 1).padStart(2, '0')
);

export const BIRTH_MONTH_OPTIONS = [
  { value: '01', label: 'Jan' },
  { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' },
  { value: '04', label: 'Apr' },
  { value: '05', label: 'May' },
  { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' },
  { value: '08', label: 'Aug' },
  { value: '09', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' },
] as const;

const MIN_BIRTH_YEAR = 1920;
const MAX_BIRTH_YEAR = new Date().getFullYear();

export const BIRTH_YEAR_OPTIONS = Array.from(
  { length: MAX_BIRTH_YEAR - MIN_BIRTH_YEAR + 1 },
  (_, index) => String(MAX_BIRTH_YEAR - index)
);

export function toDateInputValue(value?: string | Date): string {
  if (!value) return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return format(value, 'yyyy-MM-dd');
  }

  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) return trimmed.slice(0, 10);

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return '';
  return format(parsed, 'yyyy-MM-dd');
}

export function toBirthDateParts(
  value?: string | Date
): { birthDay: string; birthMonth: string; birthYear: string } {
  const normalized = toDateInputValue(value);
  if (!normalized) {
    return { birthDay: '', birthMonth: '', birthYear: '' };
  }

  const [birthYear = '', birthMonth = '', birthDay = ''] = normalized.split('-');
  return { birthDay, birthMonth, birthYear };
}

export type BirthDateParts = {
  birthDay: string;
  birthMonth: string;
  birthYear: string;
};

export function getBirthDateValidationMessage(
  values: BirthDateParts,
  referenceDate: Date = new Date()
): string | null {
  const birthDay = Number(values.birthDay);
  const birthMonth = Number(values.birthMonth);
  const birthYear = Number(values.birthYear);

  if (
    !Number.isInteger(birthDay) ||
    !Number.isInteger(birthMonth) ||
    !Number.isInteger(birthYear)
  ) {
    return 'Enter a valid date of birth';
  }

  const birthDate = new Date(Date.UTC(birthYear, birthMonth - 1, birthDay));
  const isRealCalendarDate =
    birthDate.getUTCFullYear() === birthYear &&
    birthDate.getUTCMonth() === birthMonth - 1 &&
    birthDate.getUTCDate() === birthDay;

  if (!isRealCalendarDate) {
    return 'Enter a valid date of birth';
  }

  const today = Date.UTC(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );
  if (birthDate.getTime() > today) {
    return 'Date of birth cannot be in the future';
  }

  return null;
}

export function buildBirthDateString(values: BirthDateParts): string {
  return `${values.birthYear}-${values.birthMonth}-${values.birthDay}`;
}

// Occupation categories for Ghana market
// Weighted: ~70% informal sector, ~20% entry-level/professionals, ~10% students, ~7-10% corporate
export const OCCUPATION_CATEGORIES = {
  'Market & Retail': [
    'Market Trader',
    'Shop Owner',
    'Provision Store Owner',
    'Mobile Money Vendor',
    'Street Vendor',
    'Food Vendor',
    'Kiosk Operator',
    'Boutique Owner',
    'Cosmetics Seller',
    'Phone Accessories Dealer',
    'Second-hand Clothes Dealer',
  ],
  'Personal Services': [
    'Barber',
    'Hairdresser',
    'Beautician',
    'Nail Technician',
    'Makeup Artist',
    'Seamstress',
    'Tailor',
    'Fashion Designer',
    'Laundry Service Operator',
    'Cobbler',
  ],
  'Food & Hospitality': [
    'Restaurant Owner',
    'Chop Bar Operator',
    'Baker',
    'Caterer',
    'Food Seller',
    'Fast Food Vendor',
    'Bar Operator',
    'Chef',
    'Waiter/Waitress',
  ],
  'Skilled Trades': [
    'Mechanic',
    'Electrician',
    'Plumber',
    'Carpenter',
    'Mason',
    'Painter',
    'Welder',
    'Tiler',
    'AC Technician',
    'Phone Repairer',
    'Computer Repairer',
    'Appliance Repairer',
    'Auto Electrician',
  ],
  'Transportation': [
    'Taxi Driver',
    'Uber/Bolt Driver',
    'Trotro Driver',
    'Trotro Mate',
    'Truck Driver',
    'Delivery Rider',
    'Commercial Motorbike Rider',
    'Bus Driver',
  ],
  'Creative & Media': [
    'Photographer',
    'Videographer',
    'DJ',
    'Event Decorator',
    'Graphic Designer',
    'Artist',
    'Musician',
    'Sound Engineer',
    'Content Creator',
  ],
  'Education': [
    'Teacher',
    'Lecturer',
    'Professor',
    'Private Tutor',
    'Headmaster/Headmistress',
    'School Administrator',
    'Librarian',
    'Research Assistant',
    'Teaching Assistant',
  ],
  'Healthcare': [
    'Doctor',
    'Nurse',
    'Midwife',
    'Pharmacist',
    'Dentist',
    'Optometrist',
    'Physiotherapist',
    'Lab Technician',
    'Radiographer',
    'Medical Officer',
    'Community Health Worker',
    'Pharmacy Technician',
    'Veterinarian',
    'Nutritionist',
    'Psychologist',
  ],
  'Finance & Business': [
    'Accountant',
    'Banker',
    'Bank Teller',
    'Auditor',
    'Financial Analyst',
    'Insurance Agent',
    'Microfinance Officer',
    'Economist',
    'Sales Representative',
    'Marketing Executive',
    'Business Owner',
    'Procurement Officer',
  ],
  'Public Service': [
    'Civil Servant',
    'Police Officer',
    'Immigration Officer',
    'Customs Officer',
    'Fire Service Officer',
    'National Service Personnel',
    'Military Personnel',
    'DVLA Officer',
    'GRA Officer',
  ],
  'Corporate & Professional': [
    'Manager',
    'Executive',
    'Administrative Assistant',
    'HR Officer',
    'Operations Manager',
    'Project Manager',
    'Software Developer',
    'IT Support',
    'Network Administrator',
    'Data Analyst',
    'Engineer',
    'Architect',
    'Lawyer',
    'Barrister',
    'Surveyor',
    'Quantity Surveyor',
    'Real Estate Agent',
    'Consultant',
    'Journalist',
    'Editor',
    'Pilot',
    'Air Traffic Controller',
    'Statistician',
    'Secretary',
    'Receptionist',
  ],
  'Agriculture & Fishing': [
    'Farmer',
    'Farm Manager',
    'Agricultural Officer',
    'Livestock Keeper',
    'Fisherman',
    'Fishermonger',
  ],
  'Students & Entry-Level': [
    'University Student',
    'Polytechnic Student',
    'Vocational Student',
    'Apprentice',
    'Intern',
  ],
  'Other': [
    'Clergy',
    'Security Guard',
    'Cleaner',
    'Gardener',
    'Call Center Agent',
    'Freelancer',
    'Retiree',
    'Unemployed',
    'Homemaker',
  ],
} as const;

// Flat list of all occupations for backward compatibility
export const EMPLOYMENTS = Object.values(OCCUPATION_CATEGORIES).flat();

export const EMPLOYMENTS_STATUS = [
  { value: 'EMPLOYED', label: 'I have a regular job' },
  { value: 'SELF_EMPLOYED', label: 'I work for myself' },
  { value: 'UNEMPLOYED', label: "I'm not working right now" },
  { value: 'STUDENT', label: "I'm a student" },
  { value: 'RETIRED', label: "I'm retired" },
] as const;

export interface PhoneNumberBreakdown {
  countryCode: CountryCode;
  areaCode: string;
  number: string;
  formatted: string;
  isValid: boolean;
  cleaned: string;
}

export function capitalizeFirstLetter(value: string): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function parsePhoneNumber(phoneNumber: string): PhoneNumberBreakdown {
  // Remove all non-digit characters (including spaces)
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Default breakdown object
  const breakdown: PhoneNumberBreakdown = {
    countryCode: 'GH',
    areaCode: '',
    number: '',
    formatted: phoneNumber,
    isValid: false,
    cleaned: cleaned.toString(),
  };

  // Check if it's a Ghana number (+233)
  if (cleaned.startsWith('233') && cleaned.length === 12) {
    breakdown.countryCode = 'GH';
    breakdown.areaCode = cleaned.substring(3, 5); // Next 2 digits after 233
    breakdown.number = cleaned.substring(3); // Remaining digits
    breakdown.formatted = `+233 ${
      breakdown.areaCode
    } ${breakdown.number.substring(0, 3)} ${breakdown.number.substring(3)}`;
    breakdown.isValid = true;
  }
  // Handle other common formats or add more country codes as needed
  else if (cleaned.length >= 10) {
    // Generic fallback for other international numbers
    if (cleaned.length > 10) {
      const countryCodeLength = cleaned.length - 10;
      breakdown.countryCode = 'GH'; // Generic country code
      breakdown.areaCode = cleaned.substring(
        countryCodeLength,
        countryCodeLength + 3
      );
      breakdown.number = cleaned.substring(countryCodeLength + 3);
      breakdown.formatted = `+${cleaned.substring(0, countryCodeLength)} ${
        breakdown.areaCode
      } ${breakdown.number}`;
      breakdown.isValid = true;
    }
  }

  return breakdown;
}

export function getNetworkLogo(type: string): string | null {
  const normalized = String(type || '').trim().toLowerCase();
  if (!normalized) return null;

  if (normalized.includes('telecel') || normalized.includes('vodafone')) {
    return '/assets/logos/telecel.png';
  }

  if (normalized.includes('mtn')) {
    return '/assets/logos/momo-yellow.png';
  }

  return null;
}
